// 📸 Componente de carga de imagen con OCR para boletas
// Permite subir una imagen y extraer automáticamente datos usando OCR

import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, CheckCircle, AlertCircle, Scan } from 'lucide-react';
import { ocrService, OCRResult } from '../../services/ocrService';

interface ImageUploadWithOCRProps {
  onImageUpload: (file: File) => void;
  onOCRComplete: (result: OCRResult) => void;
  onImageRemove: () => void;
  currentImage?: File | string | null;
  disabled?: boolean;
}

const ImageUploadWithOCR: React.FC<ImageUploadWithOCRProps> = ({
  onImageUpload,
  onOCRComplete,
  onImageRemove,
  currentImage,
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState<OCRResult | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inicializar preview si hay una imagen actual
  React.useEffect(() => {
    if (currentImage) {
      if (typeof currentImage === 'string') {
        setPreview(currentImage);
      } else if (currentImage instanceof File) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
        };
        reader.readAsDataURL(currentImage);
      }
    } else {
      setPreview(null);
    }
  }, [currentImage]);

  const handleFileSelect = async (file: File) => {
    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setError('Por favor, selecciona un archivo de imagen válido');
      return;
    }

    // Validar tamaño (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('La imagen es demasiado grande. Máximo 10MB');
      return;
    }

    setError(null);
    setOcrResult(null);
    setIsProcessing(true);

    try {
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Llamar callback de carga
      onImageUpload(file);

      // Procesar OCR
      console.log('🔍 Iniciando OCR...');
      const result = await ocrService.extractText(file);
      setOcrResult(result);
      onOCRComplete(result);
    } catch (err) {
      console.error('Error al procesar imagen:', err);
      setError('Error al procesar la imagen. Por favor, intenta de nuevo.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setOcrResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onImageRemove();
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-4">
      {/* Área de carga */}
      {!preview ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            transition-all duration-200
            ${isDragging 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
            disabled={disabled}
          />

          <div className="flex flex-col items-center justify-center space-y-4">
            {isProcessing ? (
              <>
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
                <p className="text-gray-700 font-medium">Procesando imagen...</p>
                <p className="text-sm text-gray-500">Extrayendo información de la boleta</p>
              </>
            ) : (
              <>
                <div className="p-4 bg-blue-100 rounded-full">
                  <Upload className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-gray-700 font-medium mb-1">
                    Clic para subir o arrastra aquí
                  </p>
                  <p className="text-sm text-gray-500">
                    JPG, PNG, WEBP (máx. 10MB)
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Scan className="w-4 h-4" />
                  <span>Lectura automática</span>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Preview de imagen */}
          <div className="relative border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
            <img
              src={preview}
              alt="Preview de boleta"
              className="w-full h-auto max-h-96 object-contain"
            />
            {!disabled && (
              <button
                onClick={handleRemove}
                className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                aria-label="Eliminar imagen"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Resultado del OCR */}
          {ocrResult && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-blue-900">Información extraída de la boleta</h4>
                <span className="ml-auto text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                  {ocrResult.confidence.toFixed(0)}% confianza
                </span>
              </div>

              {ocrResult.extractedData && (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {ocrResult.extractedData.monto && (
                    <div>
                      <span className="text-gray-600">Monto detectado:</span>
                      <span className="ml-2 font-semibold text-green-700">
                        ${ocrResult.extractedData.monto.toLocaleString('es-CL')}
                      </span>
                    </div>
                  )}
                  {ocrResult.extractedData.fecha && (
                    <div>
                      <span className="text-gray-600">Fecha detectada:</span>
                      <span className="ml-2 font-semibold text-gray-900">
                        {ocrResult.extractedData.fecha}
                      </span>
                    </div>
                  )}
                  {ocrResult.extractedData.descripcion && (
                    <div className="col-span-2">
                      <span className="text-gray-600">Descripción detectada:</span>
                      <p className="mt-1 text-gray-900 font-medium">
                        {ocrResult.extractedData.descripcion}
                      </p>
                    </div>
                  )}
                  {ocrResult.extractedData.categoria && (
                    <div className="col-span-2">
                      <span className="text-gray-600">Categoría sugerida:</span>
                      <span className="ml-2 font-semibold text-blue-700">
                        {ocrResult.extractedData.categoria}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {ocrResult.text && (
                <details className="mt-3">
                  <summary className="text-sm text-blue-700 cursor-pointer hover:text-blue-900">
                    Ver texto completo extraído
                  </summary>
                  <pre className="mt-2 p-3 bg-white rounded text-xs text-gray-700 max-h-40 overflow-auto border border-gray-200">
                    {ocrResult.text}
                  </pre>
                </details>
              )}
            </div>
          )}

          {/* Botón para cambiar imagen */}
          {!disabled && (
            <button
              onClick={handleClick}
              className="w-full px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
            >
              Cambiar imagen
            </button>
          )}
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ImageUploadWithOCR;

